import React, { Component } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { sobStatusBadge } from './sobRunPanel';

var redirectURL = require('../redirectURL');
var moment = require('moment');

// ---------------------------------------------------------------------------------------------
// User Activities > SOB Runs (client, 2026-10-09). One row per SOB run (lm_sob_run_log); selecting a
// run shows what it saw and did, for debugging and audit:
//   - Vehicle availability at run time (lm_sob_run_trucks): every truck the source returned, tagged
//     Allocated (with trip) / Used earlier today / No zone - not used / Free
//   - Trips: Kept (earlier run) / Allocated (this run, with vehicle) / Waiting (no truck)
//   - SOB deficits per transporter before and after the run, and the error of a failed run
// Props: fromDate, toDate (YYYY-MM-DD), reloadKey (bump to reload)
// ---------------------------------------------------------------------------------------------

function fmt(v) {
    if (!v) { return ""; }
    var m = moment(v);
    return m.isValid() ? m.format("DD-MMM-YYYY HH:mm") : String(v);
}

var GRID_DEFAULTS = { sortable: true, filter: true, floatingFilter: true, resizable: true };

export default class SobRunLogTab extends Component {
    constructor(props) {
        super(props);
        this.state = { runs: [], selected: null, log: null, trucks: [], loading: false, detailTab: "trucks" };
        this.runsGrid = React.createRef();
        this.trucksGrid = React.createRef();
        this.tripsGrid = React.createRef();
    }

    componentDidMount() { this.loadRuns(); }

    componentDidUpdate(prev) {
        if (prev.reloadKey !== this.props.reloadKey) { this.loadRuns(); }
    }

    loadRuns() {
        var params = {};
        if (this.props.fromDate) { params.from_date = this.props.fromDate; }
        if (this.props.toDate) { params.to_date = this.props.toDate; }
        this.setState({ loading: true });
        redirectURL.post("/lmconsignees/getSobRunLogs", params).then((r) => {
            this.setState({ runs: (r.data && r.data.records) || [], loading: false, selected: null, log: null, trucks: [] });
        }).catch(() => { this.setState({ runs: [], loading: false }); });
    }

    selectRun(row) {
        if (!row) { return; }
        this.setState({ selected: row, log: null, trucks: [] });
        redirectURL.post("/lmconsignees/getSobRunDetail", { transaction_id: row.transaction_id, run_no: row.run_no })
            .then((r) => {
                if (r.data && r.data.status === "success") {
                    this.setState({ log: r.data.log || {}, trucks: r.data.trucks || [] });
                }
            });
    }

    // Called by the parent's Export button: exports whatever the user is looking at.
    exportCsv() {
        var grid, name;
        if (this.state.selected && this.state.detailTab === "trucks" && this.trucksGrid.current) {
            grid = this.trucksGrid.current; name = "sob_run_vehicles_" + this.state.selected.transaction_id + "_run" + this.state.selected.run_no;
        } else if (this.state.selected && this.state.detailTab === "trips" && this.tripsGrid.current) {
            grid = this.tripsGrid.current; name = "sob_run_trips_" + this.state.selected.transaction_id + "_run" + this.state.selected.run_no;
        } else {
            grid = this.runsGrid.current; name = "sob_runs";
        }
        if (grid && grid.api) { grid.api.exportDataAsCsv({ fileName: name + ".csv" }); }
    }

    trips() {
        var out = [];
        ((this.state.log && this.state.log.stages) || []).forEach(function (st) {
            (st.trips || []).forEach(function (t) { out.push(Object.assign({ planning_type: st.planning_type }, t)); });
        });
        return out;
    }

    sobDeficits() {
        var out = [];
        ((this.state.log && this.state.log.stages) || []).forEach(function (st) {
            var after = {};
            (st.sob_after || []).forEach(function (a) { after[a.transporter_code + "|" + a.zone] = a; });
            (st.sob_before || []).forEach(function (b) {
                var a = after[b.transporter_code + "|" + b.zone] || {};
                out.push({ planning_type: st.planning_type, zone: b.zone, transporter: b.transporter_name,
                           code: b.transporter_code, approved: b.approved_sob, actual_before: b.actual_sob,
                           deficit_before: b.deficit, actual_after: a.actual_sob, deficit_after: a.deficit });
            });
        });
        return out;
    }

    render() {
        var runCols = [
            { headerName: "Started", field: "started_at", width: 150, valueGetter: (p) => fmt(p.data.started_at), sort: "desc" },
            { headerName: "Warehouse", field: "plant_code", width: 110 },
            { headerName: "Transaction", field: "transaction_id", width: 230 },
            { headerName: "SOB Run No", field: "run_no", width: 100 },
            { headerName: "Requested By", field: "requested_by", width: 200 },
            { headerName: "Status", field: "status", width: 160, cellRenderer: (p) => sobStatusBadge(p.value) },
            { headerName: "Truck Trips", field: "eligible_trips", width: 100 },
            { headerName: "Allocated Now", field: "new_trips", width: 110 },
            { headerName: "Kept", field: "kept_trips", width: 80 },
            { headerName: "Waiting", field: "pending_trips", width: 90 },
            { headerName: "Availability Source", field: "availability_source", width: 140 },
            { headerName: "Trucks Seen", field: "trucks_seen", width: 100 },
            { headerName: "Used Earlier Today", field: "trucks_used_today", width: 140 },
            { headerName: "Finished", field: "finished_at", width: 150, valueGetter: (p) => fmt(p.data.finished_at) },
            { headerName: "Remarks", field: "remarks", width: 420, tooltipField: "remarks" }
        ];
        var truckCols = [
            { headerName: "Stage", field: "planning_type", width: 100 },
            { headerName: "Zone", field: "zone", width: 110, valueGetter: (p) => p.data.zone || "(no zone)" },
            { headerName: "Transporter", field: "transporter_name", width: 200 },
            { headerName: "Code", field: "transporter_code", width: 90 },
            { headerName: "Vehicle Type", field: "vehicle_type", width: 130 },
            { headerName: "Vehicle Number", field: "vehicle_number", width: 140 },
            { headerName: "Tentative Arrival", field: "tentative_arrival", width: 140 },
            { headerName: "Source", field: "source", width: 90 },
            { headerName: "Status", field: "status", width: 160,
              cellStyle: (p) => ({ color: p.value === "Allocated" ? "#2e7d32" : (p.value === "Free" ? "#1565c0" : "#757575"), fontWeight: 600 }) },
            { headerName: "Trip", field: "trip_no", width: 80 }
        ];
        var tripCols = [
            { headerName: "Stage", field: "planning_type", width: 100 },
            { headerName: "Trip", field: "trip_no", width: 80 },
            { headerName: "Zone", field: "zone", width: 110 },
            { headerName: "Vehicle Type", field: "vehicle_type", width: 130 },
            { headerName: "CMT", field: "volume", width: 90 },
            { headerName: "Outcome", field: "outcome", width: 110 },
            { headerName: "Transporter", field: "transporter_code", width: 110 },
            { headerName: "Vehicle Number", field: "vehicle_number", width: 140 }
        ];
        var sobCols = [
            { headerName: "Stage", field: "planning_type", width: 100 },
            { headerName: "Zone", field: "zone", width: 110 },
            { headerName: "Transporter", field: "transporter", width: 200 },
            { headerName: "Code", field: "code", width: 90 },
            { headerName: "Approved %", field: "approved", width: 110 },
            { headerName: "Actual % Before", field: "actual_before", width: 130 },
            { headerName: "Deficit Before", field: "deficit_before", width: 120 },
            { headerName: "Actual % After", field: "actual_after", width: 120 },
            { headerName: "Deficit After", field: "deficit_after", width: 110 }
        ];
        var sel = this.state.selected;
        var log = this.state.log;
        var tabBtn = (key, label) => (
            <button type="button" className={"pill-btn " + (this.state.detailTab === key ? "pill-btn-info" : "pill-btn-info pill-btn-soft")}
                    onClick={() => this.setState({ detailTab: key })}>{label}</button>
        );
        return (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: "8px" }}>
                <div style={{ fontSize: "12px", color: "#78909c" }}>
                    {this.state.loading ? "Loading..." : (this.state.runs.length + " SOB run(s). Select a run to see the vehicles it saw and what it did.")}
                </div>
                <div className="ag-theme-balham" style={{ height: sel ? "38%" : "100%", minHeight: "180px" }}>
                    <AgGridReact ref={this.runsGrid} modules={AllModules} columnDefs={runCols} rowData={this.state.runs}
                                 defaultColDef={GRID_DEFAULTS} rowHeight={38} headerHeight={40} rowSelection="single"
                                 enableBrowserTooltips={true}
                                 onRowClicked={(e) => this.selectRun(e.data)} />
                </div>
                {sel ?
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: "260px", gap: "6px" }}>
                        <div className="d-flex align-items-center" style={{ gap: "8px", flexWrap: "wrap" }}>
                            <b style={{ fontSize: "13px" }}>{sel.transaction_id} - run {sel.run_no}</b>
                            {tabBtn("trucks", "Vehicle availability (" + this.state.trucks.length + ")")}
                            {tabBtn("trips", "Trips (" + this.trips().length + ")")}
                            {tabBtn("sob", "SOB deficits")}
                            <span style={{ cursor: "pointer", color: "#90a4ae", marginLeft: "auto" }} onClick={() => this.setState({ selected: null })}>close ✕</span>
                        </div>
                        {log && log.error ?
                            <pre style={{ maxHeight: "90px", overflow: "auto", fontSize: "11px", background: "#fff3f3", color: "#b71c1c", padding: "6px", margin: 0 }}>{log.error}</pre>
                            : ""}
                        <div className="ag-theme-balham" style={{ flex: 1, minHeight: "200px" }}>
                            {this.state.detailTab === "trucks" ?
                                <AgGridReact ref={this.trucksGrid} modules={AllModules} columnDefs={truckCols} rowData={this.state.trucks}
                                             defaultColDef={GRID_DEFAULTS} rowHeight={34} headerHeight={40}
                                             overlayNoRowsTemplate={"No trucks recorded for this run (dedicated/courier runs use no real trucks)"} />
                                : this.state.detailTab === "trips" ?
                                <AgGridReact ref={this.tripsGrid} modules={AllModules} columnDefs={tripCols} rowData={this.trips()}
                                             defaultColDef={GRID_DEFAULTS} rowHeight={34} headerHeight={40} />
                                :
                                <AgGridReact modules={AllModules} columnDefs={sobCols} rowData={this.sobDeficits()}
                                             defaultColDef={GRID_DEFAULTS} rowHeight={34} headerHeight={40} />
                            }
                        </div>
                    </div>
                    : ""}
            </div>
        );
    }
}
